import { AppConstants } from "@/app/lib/util/AppConstants";

export type TraineeMobileChatPanel = "contacts" | "chat";

export interface TraineeMobileChatPanelState {
  panel: TraineeMobileChatPanel;
  sessionId?: string;
}

const STORAGE_KEY = AppConstants.SESSION_NAMES.MOBILE_CHAT_PANEL;

export const isMobileTraineeViewport = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }
  return window.matchMedia("(max-width: 768px)").matches;
};

/** Session id from `/trainee/{sessionId}`; null on `/trainee` only. */
export const getSessionIdFromTraineePath = (
  pathname: string,
): string | null => {
  const segments = pathname.split("/").filter(Boolean);
  if (segments[0] !== "trainee" || segments.length < 2) {
    return null;
  }
  return segments[1] ?? null;
};

export const readTraineeMobileChatPanel =
  (): TraineeMobileChatPanelState | null => {
    if (typeof window === "undefined") {
      return null;
    }
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw) as TraineeMobileChatPanelState;
      if (parsed?.panel === "contacts" || parsed?.panel === "chat") {
        return parsed;
      }
    } catch {
      /* ignore */
    }
    return null;
  };

export const writeTraineeMobileChatPanel = (
  state: TraineeMobileChatPanelState,
): void => {
  if (typeof window === "undefined") {
    return;
  }
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore */
  }
};

export const clearTraineeMobileChatPanel = (): void => {
  if (typeof window === "undefined") {
    return;
  }
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
};

/**
 * Mobile stream chat: show contact list overlay when true, chat pane when false.
 * Fresh login → contacts. Return from minimize while in chat → chat.
 */
export const shouldShowTraineeMobileContactList = (
  pathname: string,
): boolean => {
  const sessionId = getSessionIdFromTraineePath(pathname);
  const stored = readTraineeMobileChatPanel();

  if (stored?.panel === "chat") {
    if (sessionId) {
      return false;
    }
    return true;
  }

  return true;
};
